// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

const STAGES = ["Discovery", "Qualification", "Proposal", "Negotiation", "Closed"];

const DEALS = [
  { id: 1, company: "Canstorr Commodities", value: 45000, product: "CropPatrol", probability: 80, owner: "Tyson Becker", daysInStage: 5, stage: "Negotiation", contact: "Mike Thompson", notes: "Partnership expansion. Final terms under review." },
  { id: 2, company: "Western Mining Corp", value: 35000, product: "OreSync", probability: 60, owner: "Lee Mehling", daysInStage: 8, stage: "Proposal", contact: "Sarah Chen", notes: "Sent integration proposal. Awaiting CTO approval." },
  { id: 3, company: "Prairie Fleet Services", value: 18000, product: "Forge Fleet", probability: 40, owner: "Lucas Mason", daysInStage: 3, stage: "Qualification", contact: "Dave Richards", notes: "45 vehicle fleet. Demo scheduled next week." },
  { id: 4, company: "Northern Health Authority", value: 52000, product: "VitaSync", probability: 75, owner: "Des Bachynski", daysInStage: 12, stage: "Proposal", contact: "Jennifer Walsh", notes: "Beta testing positive. Scaling to full deployment." },
  { id: 5, company: "AutoMax Dealers Group", value: 28000, product: "LotSync", probability: 25, owner: "Lucas Mason", daysInStage: 14, stage: "Discovery", contact: "Robert Kim", notes: "3 dealership locations. Early conversations." },
  { id: 6, company: "GreenLeaf Cannabis Co", value: 22000, product: "BudLedger", probability: 50, owner: "Lucas Mason", daysInStage: 6, stage: "Qualification", contact: "Amanda Foster", notes: "Compliance-focused. Need to address pricing concerns." },
  { id: 7, company: "Pacific Tire Warehouse", value: 15000, product: "TreadOps", probability: 55, owner: "Lucas Mason", daysInStage: 9, stage: "Proposal", contact: "Tom Bradley", notes: "Single location. Wants full POS integration." },
  { id: 8, company: "Apex Insurance Brokers", value: 38000, product: "PolicyForge", probability: 85, owner: "Lucas Mason", daysInStage: 4, stage: "Negotiation", contact: "Lisa Nguyen", notes: "12-person team. Close to signing. Finalizing SLA." },
  { id: 9, company: "BeautyBliss Salons", value: 12000, product: "GlowDesk", probability: 35, owner: "Lucas Mason", daysInStage: 2, stage: "Discovery", contact: "Priya Sharma", notes: "3 salon locations. Initial interest in scheduling." },
  { id: 10, company: "HealthFirst Clinics", value: 48000, product: "VitaSync", probability: 100, owner: "Des Bachynski", daysInStage: 0, stage: "Closed", contact: "Karen Phillips", notes: "Signed 2-year contract. Onboarding started." },
];

export default function DealsPipelinePage() {
  const [deals, setDeals] = useState(DEALS);
  const [view, setView] = useState("pipeline");
  const [movingDeal, setMovingDeal] = useState(null);

  const totalPipeline = deals.filter(d => d.stage !== "Closed").reduce((s, d) => s + d.value, 0);
  const weightedValue = deals.filter(d => d.stage !== "Closed").reduce((s, d) => s + d.value * d.probability / 100, 0);
  const activeDeals = deals.filter(d => d.stage !== "Closed");
  const avgDealSize = activeDeals.length > 0 ? Math.round(totalPipeline / activeDeals.length) : 0;
  const closedWon = deals.filter(d => d.stage === "Closed");
  const winRate = deals.length > 0 ? Math.round(closedWon.length / deals.length * 100) : 0;
  const avgCycle = Math.round(deals.reduce((s, d) => s + d.daysInStage, 0) / deals.length);

  const moveDeal = (dealId, newStage) => {
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, stage: newStage, daysInStage: 0 } : d));
    setMovingDeal(null);
  };

  const stageValue = (stage) => deals.filter(d => d.stage === stage).reduce((s, d) => s + d.value, 0);

  return (
    <div>
      <div className="page-header">
        <div><h1>Deals Pipeline</h1><p>{activeDeals.length} active deals</p></div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className={"btn " + (view === "pipeline" ? "btn-primary" : "btn-outline")} onClick={() => setView("pipeline")}>Pipeline</button>
          <button className={"btn " + (view === "table" ? "btn-primary" : "btn-outline")} onClick={() => setView("table")}>Table</button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total Pipeline Value</div><div className="stat-value">${(totalPipeline / 1000).toFixed(0)}K</div></div>
        <div className="stat-card"><div className="stat-label">Weighted Value</div><div className="stat-value" style={{ color: "#10b981" }}>${(weightedValue / 1000).toFixed(0)}K</div></div>
        <div className="stat-card"><div className="stat-label">Avg Deal Size</div><div className="stat-value">${(avgDealSize / 1000).toFixed(0)}K</div></div>
        <div className="stat-card"><div className="stat-label">Win Rate</div><div className="stat-value" style={{ color: "#10b981" }}>{winRate}%</div><div style={{ fontSize: 12, color: "#64748b" }}>Avg cycle: {avgCycle} days</div></div>
      </div>

      {view === "pipeline" ? (
        <div className="kanban-board">
          {STAGES.map(stage => {
            const stageDeals = deals.filter(d => d.stage === stage);
            const sv = stageValue(stage);
            return (
              <div key={stage} className="kanban-column">
                <div className="kanban-column-header">
                  <h3>{stage}</h3>
                  <span className="count">{stageDeals.length} (${(sv / 1000).toFixed(0)}K)</span>
                </div>
                <div className="kanban-cards">
                  {stageDeals.map(deal => (
                    <div key={deal.id} className="kanban-card" onClick={() => setMovingDeal(movingDeal === deal.id ? null : deal.id)}>
                      <div className="kanban-card-title">{deal.company}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#10b981", margin: "6px 0" }}>${deal.value.toLocaleString()}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>{deal.product}</div>
                      <div className="kanban-card-meta">
                        <span style={{ fontSize: 11, color: "#64748b" }}>{deal.probability}% prob</span>
                        <span className="assignee-tag">{deal.owner.split(" ").map(n => n[0]).join("")}</span>
                        <span style={{ fontSize: 11, color: deal.daysInStage > 10 ? "#ef4444" : "#64748b" }}>{deal.daysInStage}d</span>
                      </div>
                      {movingDeal === deal.id && (
                        <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {STAGES.filter(s => s !== stage).map(s => (
                            <button key={s} className="btn btn-outline btn-sm" style={{ fontSize: 10, padding: "2px 6px" }} onClick={(e) => { e.stopPropagation(); moveDeal(deal.id, s); }}>{s}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card" style={{ marginTop: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "2px solid #334155", color: "#94a3b8" }}>Company</th>
                <th style={{ textAlign: "right", padding: "10px 12px", borderBottom: "2px solid #334155", color: "#94a3b8" }}>Value</th>
                <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "2px solid #334155", color: "#94a3b8" }}>Product</th>
                <th style={{ textAlign: "center", padding: "10px 12px", borderBottom: "2px solid #334155", color: "#94a3b8" }}>Prob</th>
                <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "2px solid #334155", color: "#94a3b8" }}>Stage</th>
                <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "2px solid #334155", color: "#94a3b8" }}>Owner</th>
                <th style={{ textAlign: "center", padding: "10px 12px", borderBottom: "2px solid #334155", color: "#94a3b8" }}>Days</th>
              </tr>
            </thead>
            <tbody>
              {deals.map(deal => (
                <tr key={deal.id}>
                  <td style={{ padding: "10px 12px", borderBottom: "1px solid #1e293b" }}>
                    <div style={{ fontWeight: 600, color: "#e2e8f0" }}>{deal.company}</div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>{deal.contact}</div>
                  </td>
                  <td style={{ padding: "10px 12px", borderBottom: "1px solid #1e293b", textAlign: "right", fontWeight: 700, color: "#10b981" }}>${deal.value.toLocaleString()}</td>
                  <td style={{ padding: "10px 12px", borderBottom: "1px solid #1e293b", color: "#e2e8f0" }}>{deal.product}</td>
                  <td style={{ padding: "10px 12px", borderBottom: "1px solid #1e293b", textAlign: "center", color: deal.probability >= 70 ? "#10b981" : deal.probability >= 40 ? "#eab308" : "#64748b" }}>{deal.probability}%</td>
                  <td style={{ padding: "10px 12px", borderBottom: "1px solid #1e293b" }}>
                    <span className="badge" style={{ background: deal.stage === "Closed" ? "#10b98122" : "#3b82f622", color: deal.stage === "Closed" ? "#10b981" : "#3b82f6" }}>{deal.stage}</span>
                  </td>
                  <td style={{ padding: "10px 12px", borderBottom: "1px solid #1e293b", color: "#94a3b8" }}>{deal.owner}</td>
                  <td style={{ padding: "10px 12px", borderBottom: "1px solid #1e293b", textAlign: "center", color: deal.daysInStage > 10 ? "#ef4444" : "#94a3b8" }}>{deal.daysInStage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pipeline Value by Stage */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><h3>Pipeline Value by Stage</h3></div>
        <div className="chart-bar-container">
          {STAGES.map(stage => {
            const sv = stageValue(stage);
            const maxVal = Math.max(...STAGES.map(s => stageValue(s)));
            return (
              <div key={stage} className="chart-bar-row">
                <span className="chart-bar-label" style={{ width: 110 }}>{stage}</span>
                <div className="chart-bar-track">
                  <div className={"chart-bar-fill" + (stage === "Closed" ? "" : " blue")} style={{ width: maxVal > 0 ? (sv / maxVal * 100) + "%" : "0%" }}>${(sv / 1000).toFixed(0)}K ({deals.filter(d => d.stage === stage).length} deals)</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Deals by Product */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><h3>Revenue by Product</h3></div>
        {[...new Set(deals.map(d => d.product))].sort().map(product => {
          const productDeals = deals.filter(d => d.product === product);
          const totalVal = productDeals.reduce((s, d) => s + d.value, 0);
          return (
            <div key={product} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #334155" }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#e2e8f0" }}>{product}</span>
                <span style={{ fontSize: 12, color: "#64748b", marginLeft: 8 }}>{productDeals.length} deal{productDeals.length !== 1 ? "s" : ""}</span>
              </div>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#10b981" }}>${totalVal.toLocaleString()}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
